(() => {
  angular
    .module('gm.common.vaults')
    .factory('GmyVaults', GmyVaults);

  function GmyVaults(
    promiseCache,
    gmApi,
    btgUser,
    gettextCatalog
  ) {
    'ngInject';

    const VAULTS_INFO = [{
      id: 5,
      class: 'toronto',
      countryFlagClass: 'ca',
      country: 'Canada',
      isDefault: true,
      name: 'Toronto',
    }, {
      id: 3,
      class: 'new-york',
      countryFlagClass: 'us',
      country: 'USA',
      name: 'New York',
    }, {
      id: 2,
      class: 'london',
      countryFlagClass: 'gb',
      country: 'United Kingdom',
      name: 'London',
    }, {
      id: 6,
      class: 'zurich',
      countryFlagClass: 'ch',
      country: 'Switzerland',
      name: 'Zurich',
    }, {
      id: 1,
      class: 'hong-kong',
      countryFlagClass: 'hk',
      country: null,
      name: 'Hong Kong',
    }, {
      id: 4,
      class: 'singapore',
      countryFlagClass: 'sg',
      country: null,
      name: 'Singapore',
    }, {
      id: 7,
      class: 'dubai',
      countryFlagClass: 'ae',
      country: 'United Arab Emirates',
      name: 'Dubai',
    }, {
      id: 8,
      class: 'london-pp',
      countryFlagClass: 'gbpp',
      country: 'United Kingdom',
      name: 'London - PayPal',
      limited: {
        description: gettextCatalog.getString('Segregated PayPal only vault, cannot redeem out with methods other than back to original PayPal account'),
        supportUrl: '/customer/en/portal/articles/2579739',
      },
    }, {
      id: 9,
      class: 'ottawa',
      countryFlagClass: 'ca',
      country: 'Canada',
      name: 'Ottawa',
    }];

    return {
      getStatement,
      getStatementPDFUrl,
      getStatements,
      getVaultBaseInfoById,
      getVaultById,
      getVaults,
      getVaultsAndSummary,
      getWealthBalance,
      sendStatementEmail,
    };

    function areVaultsEmpty(vaults) {
      let vaultsEmpty = true;
      vaults.forEach((vault) => {
        if (vault.spendable > 0) {
          vaultsEmpty = false;
        }
      });
      return vaultsEmpty;
    }

    function cachedLoad(bustCache) {
      return promiseCache({
        bustCache,
        key: 'vaults',
        promise: loadVaultsAndBalances,
        ttl: 600000
      });
    }

    function getItemById(list, id) {
      return list.find(item => (item.vaultId === id));
    }

    function getStatement(id) {
      return gmApi.get(`users/statements/${id}`)
        .then((data) => {
          const statement = data.payload[0];

          statement.r[0].total_gold = statement.r[0].total_gold.replace(',', '');

          statement.balances.map((vault) => {
            vault.change = vault.change.replace(',', '');
            vault.balance = vault.balance.replace(',', '');
            if (!vault.custodian) {
              vault.custodian = 'Brinks';
            }
            vault.custodianFull = mapCustodian(vault.custodian);
            return vault;
          });

          if ('transactions' in statement) {
            statement.transactions.map((tx) => {
              tx.value = tx.value.replace(',', '');
              tx.tax_basis = tx.tax_basis.replace(',', '');
              return tx;
            });
          }

          statement.monthly = !!data.month;

          return statement;
        });
    }

    function getStatementPDFUrl(id) {
      return gmApi.get(`users/statements/${id}/pdf`);
    }

    function getStatements() {
      return promiseCache({
        promise: () => gmApi.get('users/statements'),
        ttl: 3600000
      });
    }

    function getVaultBaseInfoById(id) {
      return VAULTS_INFO.find(_ => _.id === id) || {};
    }

    function getVaultById(id) {
      return cachedLoad().then(({ vaults }) => vaults.find(v => v.id === id));
    }

    function getVaultClassById(id) {
      const vault = VAULTS_INFO.find(_ => _.id === id);
      return vault ? vault.class : undefined;
    }

    function getVaultFlagClassById(id) {
      const vault = VAULTS_INFO.find(_ => _.id === id);
      return vault ? vault.countryFlagClass : undefined;
    }

    function getVaultLimitedById(id) {
      const vault = VAULTS_INFO.find(_ => _.id === id);
      return vault ? vault.limited : undefined;
    }

    function getVaults(bustCache) {
      return cachedLoad(bustCache).then(({ vaults }) => vaults);
    }

    function getVaultsAndSummary() {
      return cachedLoad();
    }

    function getWealthBalance() {
      return gmApi.get('aurum/user/wealthBalances');
    }

    function loadVaultsAndBalances() {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      const time = Math.round(date.getTime() / 1000);

      return gmApi.get('aurum/user/balances', { params: { time } }, { auth: true })
      .then(({ vaults, spendable, settled, settledEarlier, reserved, equity }) => {
        let _vaults = vaults.map(({
          id, name, country: countryCode, countryName: country, custodian
        }) => ({
          id,
          name,
          custodian,
          custodianFull: mapCustodian(custodian),
          isDefault: id === 5,
          countryCode,
          country,
          countryFlagClass: getVaultFlagClassById(id),
          class: getVaultClassById(id),
          limited: getVaultLimitedById(id),
          spendable: getItemById(spendable, id).amount,
          settled: getItemById(settled, id).amount,
          reserved: getItemById(reserved, id).amount,
          change: {
            daily: getItemById(settled, id).amount - getItemById(settledEarlier, id).amount
          }
        }));

        _vaults = sortVaults(_vaults);

        const vaultsSummary = {};
        vaultsSummary.change = {
          daily: _vaults.reduce((sum, v) => sum + v.change.daily, 0)
        };

        vaultsSummary.sum = _vaults.reduce((sum, v) => sum + v.settled, 0);
        vaultsSummary.totalReserved = _vaults.reduce((sum, v) => sum + v.reserved, 0);
        vaultsSummary.equity = equity;

        const vaultLabels = btgUser.getProperty('vaultLabels');
        if (vaultLabels) {
          _vaults.forEach((v) => {
            v.label = vaultLabels[v.id] || v.name;
          });
        } else {
          _vaults.forEach((v) => {
            v.label = v.name;
          });
        }

        return {
          vaultsSummary,
          vaults: _vaults,
        };
      });
    }

    function mapCustodian(custodian) {
      switch (custodian) {
        case 'RCM':
          return 'Royal Canadian Mint';
        default:
          return custodian;
      }
    }

    function sendStatementEmail(id) {
      return gmApi.post(`users/statements/${id}`, {}, {
        auth: true
      });
    }

    function sortVaults(vaults) {
      if (areVaultsEmpty(vaults)) {
        vaults.sort((a, b) => b.isDefault - a.isDefault);
      } else {
        vaults.sort((a, b) => b.spendable - a.spendable);
      }
      return vaults;
    }
  }
})();
